import type {
  AssistantAnswerView,
  AssistantAskRequest,
  AssistantFeedbackRequest,
  AssistantHistoryTurn,
} from 'shared-models/browser';
import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';

const RATE_LIMITED_MESSAGE = 'הגעתם למכסת השאלות לשעה. נסו שוב בעוד כמה דקות.';
const UNAVAILABLE_MESSAGE = 'העוזר העסקי אינו זמין כרגע.';

type StatusCarryingError = { response?: { status?: number } };

// Maps the transport failure to a user-facing Hebrew message; status wins over the server text.
const toAssistantErrorMessage = (error: unknown): string => {
  const status = (error as StatusCarryingError).response?.status;
  if (status === 429) return RATE_LIMITED_MESSAGE;
  if (status === 503) return UNAVAILABLE_MESSAGE;
  return extractServiceErrorMessage(error, 'Assistant request failed');
};

class AssistantService {
  async ask(
    tenantId: string,
    question: string,
    history: AssistantHistoryTurn[] = [],
    signal?: AbortSignal,
  ): Promise<string> {
    const body: AssistantAskRequest = history.length ? { question, history } : { question };
    try {
      const response = await authenticatedInstance.post<AssistantAnswerView>(
        `/assistant/tenant/${encodeURIComponent(tenantId)}/chat`,
        body,
        { signal },
      );
      return response.data.answer;
    } catch (error: unknown) {
      throw new Error(toAssistantErrorMessage(error));
    }
  }

  async sendFeedback(tenantId: string, message: string): Promise<void> {
    const body: AssistantFeedbackRequest = { message };
    try {
      await authenticatedInstance.post(`/assistant/tenant/${encodeURIComponent(tenantId)}/feedback`, body);
    } catch (error: unknown) {
      throw new Error(toAssistantErrorMessage(error));
    }
  }
}

export const assistantService = new AssistantService();
